import { Mission } from "@/services/clientDataService";
import {
  computeVolumeDiscount,
  computeExceptionalDiscount,
  computeProjectManagement,
} from "@/utils/pricingCore";

export interface FinancialData {
  totalInvestment: number;
  totalCostWithProjectDuration: number;
  totalGainsProjectDuration: number;
  netBenefitProjectDuration: number;
  globalROI: number;
  savedDaysPerYear: number;
  equivalentFTE: number;
  amortizationMonths: number;
  annualCost: number;
  annualCostWithTemporality: number;
  annualCostRecurring: number;
  projectDurationYears: number;
  totalCostWith3Years: number;
  totalGains3Years: number;
  netBenefit3Years: number;
}

const CONSTANTS = {
  HOURLY_RATE: 44,
  WORKING_DAYS_PER_YEAR: 219.96, // 18,33j/mois × 12 mois (base moyenne)
  WORKING_HOURS_PER_DAY: 7,
};

type DisabledVolumeDiscounts = {
  "50k"?: boolean;
  "100k"?: boolean;
  "250k"?: boolean;
  "350k"?: boolean;
  "550k"?: boolean;
};

export function getMissionROIData(mission: Mission): Record<string, unknown> {
  const missionData = mission as Mission & {
    roi_data?: Record<string, unknown>;
    complete_json?: { roi_data?: Record<string, unknown> };
  };
  return missionData.roi_data || missionData.complete_json?.roi_data || {};
}

export function calculateMissionAnnualSavings(mission: Mission, hourlyRate?: number): number {
  const roiData = getMissionROIData(mission);

  const annualSaving = roiData.annual_saving_eur;
  if (typeof annualSaving === "number") return annualSaving;

  const savings = roiData.savings;
  if (
    savings &&
    typeof savings === "object" &&
    "annual" in savings &&
    typeof (savings as { annual?: unknown }).annual === "number"
  ) {
    return (savings as { annual: number }).annual;
  }

  const durationDays = parseFloat((mission.duration?.split(" ")[0] || "10").replace(",", "."));
  const effectiveHourlyRate = hourlyRate ?? CONSTANTS.HOURLY_RATE;
  return durationDays * effectiveHourlyRate * CONSTANTS.WORKING_HOURS_PER_DAY;
}

export function calculateMissionSavedDaysPerYear(mission: Mission, hourlyRate?: number): number {
  const effectiveHourlyRate = hourlyRate ?? CONSTANTS.HOURLY_RATE;
  const annualSavings = calculateMissionAnnualSavings(mission, hourlyRate);
  const days = (annualSavings / effectiveHourlyRate) / CONSTANTS.WORKING_HOURS_PER_DAY;
  return Math.round(days * 100) / 100;
}

export function isFoundationMission(mission: Mission): boolean {
  if (mission.mission_type === "foundation") return true;
  if (mission.category && mission.category.toLowerCase().includes("fondation")) return true;

  if (!mission.mission_type && mission.title) {
    const titleLower = mission.title.toLowerCase();
    if (
      titleLower.includes("architecture") ||
      titleLower.includes("ateliers de préparation") ||
      titleLower.includes("préparation et création de la base de donnée") ||
      titleLower.includes("préparation de la base de donnée") ||
      (titleLower.includes("préparation") && titleLower.includes("bdd")) ||
      titleLower.includes("rédaction de la documentation (fin de mission)") ||
      titleLower.includes("formation du personnel")
    ) {
      return true;
    }
  }

  return false;
}

export function calculateMissionAnnualCost(
  mission: Mission,
  maintenanceRatePercent: number = 20.0
): number {
  if (isFoundationMission(mission)) return 0;

  if (mission.title && mission.title.toLowerCase().includes("gestion de projet")) return 0;
  if (mission.category && mission.category.toLowerCase().includes("gestion de projet")) return 0;

  return mission.price * (maintenanceRatePercent / 100.0);
}

export function calculateAnnualCostYears(
  mission: Mission,
  projectDurationYears: number,
  projectStartYear: number = new Date().getFullYear()
): number {
  if (!mission.mission_year || !mission.mission_month) return projectDurationYears;

  const missionEndYear = mission.mission_year;
  const projectEndYear = projectStartYear + projectDurationYears - 1;
  const annualCostStartYear = missionEndYear + 1;

  return Math.max(0, projectEndYear - annualCostStartYear + 1);
}

function getEmptyFinancialData(projectDurationYears: number): FinancialData {
  return {
    totalInvestment: 0,
    totalCostWithProjectDuration: 0,
    totalGainsProjectDuration: 0,
    netBenefitProjectDuration: 0,
    globalROI: 0,
    savedDaysPerYear: 0,
    equivalentFTE: 0,
    amortizationMonths: 0,
    annualCost: 0,
    annualCostWithTemporality: 0,
    annualCostRecurring: 0,
    projectDurationYears: projectDurationYears || 3,
    totalCostWith3Years: 0,
    totalGains3Years: 0,
    netBenefit3Years: 0,
  };
}

function safeMissionDays(mission: Mission): number {
  const days = mission.duration_days || parseFloat((mission.duration?.split(" ")[0] || "0").replace(",", "."));
  return typeof days === "number" && !isNaN(days) ? days : 0;
}

export function calculateFinancialData(
  allMissions: Mission[],
  projectDurationYears: number = 3,
  annualCostPercentage: number = 20.0,
  hourlyRate?: number,
  tjm: number = 650,
  projectManagementPercentage: number = 10.0,
  exceptionalDiscountPercentage: number = 0,
  disabledVolumeDiscounts?: DisabledVolumeDiscounts,
  designMoePrice: number = 0,
  tjm_moe: number = 1000
): FinancialData {
  if (!Array.isArray(allMissions)) {
    console.error("calculateFinancialData: allMissions must be an array");
    return getEmptyFinancialData(projectDurationYears);
  }

  let totalInvestment = 0;
  let totalAnnualCost = 0;
  let totalSavedDaysPerYear = 0;
  let totalGainsProjectDuration = 0;
  let totalAnnualCostWithTemporality = 0;

  for (const mission of allMissions) {
    if (!mission || typeof mission !== "object") continue;
    try {
      totalInvestment += mission.price || 0;

      const annualCost = calculateMissionAnnualCost(mission, annualCostPercentage);
      totalAnnualCost += annualCost;

      const annualCostYears = calculateAnnualCostYears(mission, projectDurationYears);
      totalAnnualCostWithTemporality += annualCost * annualCostYears;

      totalSavedDaysPerYear += calculateMissionSavedDaysPerYear(mission, hourlyRate);

      const annualSavings = calculateMissionAnnualSavings(mission, hourlyRate);
      totalGainsProjectDuration += annualSavings * projectDurationYears;
    } catch (error) {
      console.error("calculateFinancialData: mission processing failed", error);
    }
  }

  const totalMissionDays = allMissions.reduce((sum, mission) => {
    if (!mission || typeof mission !== "object") return sum;
    return sum + safeMissionDays(mission);
  }, 0);

  const mgmt = computeProjectManagement(totalMissionDays, tjm_moe, projectManagementPercentage);
  const managementPrice = mgmt.price;

  const devisSubtotal = totalInvestment + managementPrice + designMoePrice;
  let totalDiscountAmount = computeVolumeDiscount(devisSubtotal, disabledVolumeDiscounts).amount;
  totalDiscountAmount += computeExceptionalDiscount(devisSubtotal, exceptionalDiscountPercentage);
  const adjustedInvestment = devisSubtotal - totalDiscountAmount;

  const totalCostWithProjectDuration = adjustedInvestment + totalAnnualCostWithTemporality;
  const netBenefitProjectDuration = totalGainsProjectDuration - totalCostWithProjectDuration;
  const globalROI =
    totalCostWithProjectDuration > 0
      ? Math.round((netBenefitProjectDuration / totalCostWithProjectDuration) * 100)
      : 0;

  const equivalentFTE = Math.round((totalSavedDaysPerYear / CONSTANTS.WORKING_DAYS_PER_YEAR) * 10) / 10;

  const annualSavings = totalGainsProjectDuration / projectDurationYears;
  const amortizationMonths =
    annualSavings > 0 ? Math.max(1, Math.round((adjustedInvestment / annualSavings) * 12)) : 12;

  const totalGains3Years = (totalGainsProjectDuration / projectDurationYears) * 3;
  const totalCostWith3Years = totalInvestment + totalAnnualCost * 3;
  const netBenefit3Years = totalGains3Years - totalCostWith3Years;

  const annualCostWithTemporality =
    projectDurationYears > 0 ? Math.round(totalAnnualCostWithTemporality / projectDurationYears) : 0;

  const excludedMissions: Array<{ title: string; price: number }> = [];
  for (const mission of allMissions) {
    if (!mission || typeof mission !== "object") continue;

    let excluded = false;
    if (isFoundationMission(mission)) excluded = true;
    if (!excluded && mission.category && mission.category.toLowerCase().includes("fondation")) excluded = true;

    if (!excluded && mission.title) {
      const titleLower = mission.title.toLowerCase();
      if (titleLower.includes("gestion de projet")) excluded = true;
      if (
        !excluded &&
        (titleLower.includes("formation du personnel") ||
          (titleLower.includes("formation") && titleLower.includes("module")))
      ) {
        excluded = true;
      }
      if (!excluded && titleLower.includes("ateliers de préparation")) excluded = true;
      if (!excluded && titleLower.includes("rédaction de la documentation")) excluded = true;
    }

    if (!excluded && mission.category) {
      const categoryLower = mission.category.toLowerCase();
      if (categoryLower.includes("gestion de projet")) excluded = true;
      if (!excluded && categoryLower.includes("formation")) excluded = true;
      if (!excluded && categoryLower.includes("option")) excluded = true;
    }

    if (excluded) {
      excludedMissions.push({
        title: mission.title || "Sans titre",
        price: mission.price || 0,
      });
    }
  }

  const totalFoundationsPrice = excludedMissions.reduce((sum, mission) => sum + mission.price, 0);
  const totalBilled = totalInvestment - totalDiscountAmount + managementPrice;
  const costToApplyMaintenanceToFinal = totalBilled - managementPrice - totalFoundationsPrice;
  const annualCostRecurring = Math.max(0, costToApplyMaintenanceToFinal * (annualCostPercentage / 100));

  return {
    totalInvestment: adjustedInvestment,
    totalCostWithProjectDuration,
    totalGainsProjectDuration,
    netBenefitProjectDuration,
    globalROI,
    savedDaysPerYear: totalSavedDaysPerYear,
    equivalentFTE,
    amortizationMonths,
    annualCost: totalAnnualCost,
    annualCostWithTemporality,
    annualCostRecurring,
    projectDurationYears,
    totalCostWith3Years,
    totalGains3Years,
    netBenefit3Years,
  };
}

export function calculateIncludedOptionsDays(
  selectedMissions: Mission[],
  tjm: number = 650,
  projectManagementPercentage: number = 10.0
): number {
  if (!Array.isArray(selectedMissions) || selectedMissions.length === 0) return 0;

  const totalMissionDays = selectedMissions.reduce((sum, mission) => {
    if (!mission || typeof mission !== "object") return sum;
    const days = mission.duration_days || parseFloat((mission.duration?.split(" ")[0] || "0").replace(",", "."));
    return sum + (isNaN(days) ? 0 : days);
  }, 0);

  return Math.ceil(totalMissionDays * (projectManagementPercentage / 100));
}
